import React, { useState, useEffect } from 'react';
import { Mail, MailOpen, Trash2, RefreshCw } from 'lucide-react';
import { EmailMessage } from '../types';
import { adminFetch } from '../api';

// El HTML de correos externos se muestra aislado: iframe sin scripts y con CSP restrictiva.
function emailSrcDoc(html: string): string {
  const csp = "default-src 'none'; img-src https: data:; style-src 'unsafe-inline'; font-src https: data:";
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank"></head><body style="margin:0;font-family:sans-serif">${html}</body></html>`;
}

export function EmailInbox() {
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch('/api/emails');
      if (!res.ok) throw new Error('No se pudieron cargar los correos');
      const data = await res.json();
      setEmails(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  const markRead = async (id: string) => {
    try {
      const res = await adminFetch(`/api/emails/${id}/read`, { method: 'POST' });
      if (!res.ok) throw new Error('No se pudo marcar como leído');
      setEmails(prev => prev.map(e => e.id === id ? { ...e, read: true } : e));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteEmail = async (id: string) => {
    if (!confirm('¿Seguro que deseas eliminar este correo?')) return;
    try {
      const res = await adminFetch(`/api/emails/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('No se pudo eliminar el correo');
      setEmails(prev => prev.filter(e => e.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Mail className="text-emerald-500" /> Bandeja de Entrada
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Correos recibidos a través del webhook de Resend
          </p>
        </div>
        <button
          onClick={fetchEmails}
          disabled={loading}
          className="p-2 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
          title="Actualizar"
        >
          <RefreshCw size={18} className={loading ? "animate-spin text-slate-400" : "text-slate-600"} />
        </button>
      </div>

      <div className="p-0">
        {error && (
          <div className="m-6 p-4 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">
            {error}
          </div>
        )}

        {emails.length === 0 && !loading && !error && (
          <div className="text-center py-12 text-slate-500">
            <Mail size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="font-medium text-slate-700">No hay correos en la bandeja de entrada</p>
            <p className="text-sm mt-1">Los correos enviados a tu dominio configurado en Resend aparecerán aquí.</p>
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {emails.map((email) => (
            <div 
              key={email.id} 
              className={`p-6 transition-colors ${email.read ? 'bg-white' : 'bg-slate-50'}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  {email.read ? (
                    <MailOpen size={20} className="text-slate-400" />
                  ) : (
                    <Mail size={20} className="text-emerald-500" />
                  )}
                  <div>
                    <div className="font-semibold text-slate-800">{email.from}</div>
                    <div className="text-xs text-slate-500">
                      Para: {email.to.join(', ')} • {new Date(email.receivedAt).toLocaleString('es-AR')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!email.read && (
                    <button
                      onClick={() => markRead(email.id!)}
                      className="text-xs px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-md hover:bg-emerald-100 font-medium transition-colors border border-emerald-100"
                    >
                      Marcar como leído
                    </button>
                  )}
                  <button
                    onClick={() => deleteEmail(email.id!)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Eliminar correo"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="ml-8">
                <h3 className={`text-base mb-2 ${email.read ? 'font-medium text-slate-700' : 'font-bold text-slate-900'}`}>
                  {email.subject}
                </h3>
                {email.html ? (
                  <iframe
                    title={`Correo: ${email.subject}`}
                    sandbox="allow-popups allow-popups-to-escape-sandbox"
                    srcDoc={emailSrcDoc(email.html)}
                    className="w-full h-96 bg-white border border-slate-100 rounded-xl shadow-sm resize-y"
                  />
                ) : (
                  <div className="text-sm text-slate-600 bg-white border border-slate-100 p-4 rounded-xl shadow-sm overflow-x-auto whitespace-pre-wrap">
                    {email.text || <span className="italic text-slate-400">Sin contenido</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
